import React from 'react';
import {
  Switch as AriaSwitch,
  SwitchProps as AriaSwitchProps,
} from 'react-aria-components';
import clsx from 'clsx';
import styles from './CustomToggle.module.css';

export interface CustomToggleProps extends Omit<AriaSwitchProps, 'children'> {
  /**
   * Toggle label
   */
  label?: string;
  /**
   * Size variant
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Additional CSS class
   */
  className?: string;
}

/**
 * CustomToggle component - an accessible toggle switch using react-aria-components
 */
export const CustomToggle: React.FC<CustomToggleProps> = ({
  label,
  size = 'medium',
  className = '',
  isDisabled = false,
  ...props
}) => {
  return (
    <AriaSwitch
      className={clsx(styles.wrapper, className)}
      isDisabled={isDisabled}
      {...props}
    >
      {({ isSelected, isFocusVisible }) => (
        <div className={clsx(styles.wrapper, { [styles.wrapperDisabled]: isDisabled })}>
          <div
            className={clsx(
              styles.track,
              styles[`track${size.charAt(0).toUpperCase() + size.slice(1)}`],
              {
                [styles.trackChecked]: isSelected,
                [styles.trackDisabled]: isDisabled,
                [styles.trackFocused]: isFocusVisible,
              }
            )}
          >
            <div
              className={clsx(
                styles.handle,
                styles[`handle${size.charAt(0).toUpperCase() + size.slice(1)}`],
                {
                  [styles[`handle${size.charAt(0).toUpperCase() + size.slice(1)}Checked`]]: isSelected,
                }
              )}
            />
          </div>
          {label && (
            <span
              className={clsx(styles.label, { [styles.labelDisabled]: isDisabled })}
            >
              {label}
            </span>
          )}
        </div>
      )}
    </AriaSwitch>
  );
};

CustomToggle.displayName = 'ProdiktCustomToggle';
